import React, { useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { MODAL_KEYS } from '_constants/modals';
import { theme } from '_constants/theme';
import { selectFileTypeOptions } from '_store/fileTypes/selector';
import { selectModalDataByKey, selectModalVisibleByKey } from '_store/modals/selector';
import { hideModal } from '_store/modals/slice';
import { selectError, selectForm, selectUploading } from '_store/statements/selector';
import { createStatement, refreshForm } from '_store/statements/slice';
import { selectCustomerId } from '_store/user/selector';

import Icon from 'components/atomic/Icon';
import Text from 'components/atomic/layouts/Text';
import UploadDocumentForm from 'components/composed/forms/UploadDocumentForm';

const StyledModalBody = styled(Modal.Body)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spaces.large}px;
`;

const StyledCloseActionContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  top: ${-theme.spaces.large3X}px;
  right: 0;
  font-family: 'Inter';
  font-style: normal;
  font-size: ${theme.fontSizes.regular}px;
  font-weight: 400;
  color: ${theme.colors.white};
  cursor: pointer;
`;

const StyledIconContainer = styled.div`
  margin-right: ${theme.spaces.small2X}px;
`;

const UploadDocumentModal = () => {
  const dispatch = useDispatch();
  const visible = useSelector(selectModalVisibleByKey(MODAL_KEYS.documentUpload));
  const { productId } = useSelector(selectModalDataByKey(MODAL_KEYS.documentUpload));
  const uploading = useSelector(selectUploading);
  const formValues = useSelector(selectForm);
  const error = useSelector(selectError);
  const customerId = useSelector(selectCustomerId);
  const fileTypeOptions = useSelector(selectFileTypeOptions);

  const handleClose = () => {
    dispatch(hideModal({ key: MODAL_KEYS.documentUpload }));
  };

  const handleSubmit = useCallback(
    (values) => {
      if (values) {
        dispatch(refreshForm({ ...values }));
        dispatch(createStatement({ customerId, productId }));
      }
    },
    [dispatch, customerId, productId]
  );

  return (
    <Modal
      dialogClassName="document-upload-modal"
      show={visible}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <StyledModalBody>
        <StyledCloseActionContainer onClick={handleClose}>
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.close} color={theme.colors.white} />
          </StyledIconContainer>
          Close
        </StyledCloseActionContainer>
        <Text
          size={theme.fontSizes.largeX}
          weight={700}
          color={theme.colors.gray.primary}
          bottomSpace={theme.spaces.medium}
        >
          Upload Document
        </Text>
        <UploadDocumentForm
          error={error}
          loading={uploading}
          submitDisabled={uploading}
          fileTypeOptions={fileTypeOptions}
          defaultValues={formValues}
          onCancel={handleClose}
          onSubmit={handleSubmit}
        />
      </StyledModalBody>
    </Modal>
  );
};

export default UploadDocumentModal;
